<template>
  <div>
    <el-breadcrumb separator-icon="ArrowRight" style="margin: 16px">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>宿舍管理</el-breadcrumb-item>
      <el-breadcrumb-item>日历视图</el-breadcrumb-item>
    </el-breadcrumb>
    <el-card style="margin: 15px; min-height: calc(100vh - 111px)" class="calendar-card">
      <!-- 天数切换 -->
      <div style="margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center;" class="calendar-toolbar">
        <el-radio-group v-model="days" @change="loadCalendar" size="default">
          <el-radio-button :label="7">7天</el-radio-button>
          <el-radio-button :label="15">15天</el-radio-button>
          <el-radio-button :label="30">30天</el-radio-button>
        </el-radio-group>
        <el-button type="primary" icon="Download" @click="downloadTemplate" size="default" class="export-btn">导出日历</el-button>
      </div>

      <!-- 统计概览 -->
      <el-row :gutter="20" style="margin-bottom: 20px;" class="stat-row">
        <el-col :xs="12" :sm="6">
          <el-card style="background: #f0f9eb; border-left: 4px solid #67C23A;" class="stat-mini-card">
            <div style="font-size: 14px; color: #666;">累计入住</div>
            <div style="font-size: 28px; font-weight: bold; color: #67C23A;">{{ totalCheckin }}</div>
          </el-card>
        </el-col>
        <el-col :xs="12" :sm="6">
          <el-card style="background: #fdf6ec; border-left: 4px solid #E6A23C;" class="stat-mini-card">
            <div style="font-size: 14px; color: #666;">累计退房</div>
            <div style="font-size: 28px; font-weight: bold; color: #E6A23C;">{{ totalCheckout }}</div>
          </el-card>
        </el-col>
        <el-col :xs="12" :sm="6">
          <el-card style="background: #ecf5ff; border-left: 4px solid #409EFF;" class="stat-mini-card">
            <div style="font-size: 14px; color: #666;">平均在住</div>
            <div style="font-size: 28px; font-weight: bold; color: #409EFF;">{{ avgStay }}</div>
          </el-card>
        </el-col>
        <el-col :xs="12" :sm="6">
          <el-card style="background: #fef0f0; border-left: 4px solid #F56C6C;" class="stat-mini-card">
            <div style="font-size: 14px; color: #666;">空房率</div>
            <div style="font-size: 28px; font-weight: bold; color: #F56C6C;">
              {{ calendarData.length > 0 ? calendarData[0].occupancyRate : 0 }}%
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 日历表格 -->
      <div class="calendar-table-wrap">
        <el-table v-loading="loading" :data="calendarData" border style="width: 100%"
          highlight-current-row @current-change="handleCurrentChange">
          <el-table-column label="日期" prop="dateStr" width="110" fixed>
            <template #default="scope">
              <div :class="{'is-today': scope.row.dateStr === todayStr}">
                {{ scope.row.dateStr }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="星期" prop="weekDay" width="60" align="center" />
          <el-table-column label="入住" width="70" align="center">
            <template #default="scope">
              <el-tag v-if="scope.row.checkinCount > 0" type="success" size="small">{{ scope.row.checkinCount }}人</el-tag>
              <span v-else style="color:#999">-</span>
            </template>
          </el-table-column>
          <el-table-column label="退房" width="70" align="center">
            <template #default="scope">
              <el-tag v-if="scope.row.checkoutCount > 0" type="danger" size="small">{{ scope.row.checkoutCount }}人</el-tag>
              <span v-else style="color:#999">-</span>
            </template>
          </el-table-column>
          <el-table-column label="在住" width="70" align="center">
            <template #default="scope">
              <el-tag type="primary" size="small">{{ scope.row.stayCount }}人</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="空房" width="80" align="center">
            <template #default="scope">
              <span>{{ scope.row.emptyRooms }}</span>/<span>{{ scope.row.totalRooms }}</span>
            </template>
          </el-table-column>
          <el-table-column label="入住班级" min-width="200">
            <template #default="scope">
              <div style="display:flex;gap:4px;flex-wrap:wrap;">
                <el-tag v-for="cls in (scope.row.dayClasses || [])" :key="cls.classId"
                  :color="cls.color" style="color:#fff;border:none;" size="small">
                  {{ cls.className }}({{ cls.studentCount }}人)
                </el-tag>
                <el-tag v-for="b in (scope.row.dayBookings || [])" :key="b.bookingId"
                  :color="b.classColor" style="color:#fff;border:none;" size="small" effect="dark">
                  {{ b.className }}(预订{{ b.bedsCount }}床)
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" align="center" fixed="right">
            <template #default="scope">
              <el-button type="primary" link size="small" @click="viewDetail(scope.row)">详情</el-button>
              <el-button type="success" link size="small" @click="viewRoomPanel(scope.row)">房态</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 详情弹窗 -->
      <el-dialog v-model="detailVisible" :title="detailTitle" width="65%" class="calendar-dialog">
        <div v-if="selectedDay" style="margin-bottom:15px;">
          <el-descriptions :column="4" border size="small">
            <el-descriptions-item label="在住人数">{{ selectedDay.stayCount }}</el-descriptions-item>
            <el-descriptions-item label="空房">{{ selectedDay.emptyRooms }}/{{ selectedDay.totalRooms }}</el-descriptions-item>
            <el-descriptions-item label="入住率">{{ selectedDay.occupancyRate }}%</el-descriptions-item>
            <el-descriptions-item label="当日入住">{{ selectedDay.checkinCount }}人 / 退房 {{ selectedDay.checkoutCount }}人</el-descriptions-item>
          </el-descriptions>
        </div>
        <div class="calendar-table-wrap">
          <el-table :data="detailData" border>
            <el-table-column label="类型" width="70" align="center">
              <template #default="scope">
                <el-tag size="small" :type="scope.row.type === 'class' ? '' : 'warning'">
                  {{ scope.row.type === 'class' ? '入住' : '预订' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="班级" min-width="120">
              <template #default="scope">
                <el-tag :color="scope.row.color" style="color:#fff;border:none;" size="small">
                  {{ scope.row.className }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="人数/床位" width="90" align="center">
              <template #default="scope">
                {{ scope.row.type === 'class' ? scope.row.studentCount + '人' : scope.row.bedsCount + '床' }}
              </template>
            </el-table-column>
            <el-table-column label="入住日期" width="110" align="center">
              <template #default="scope">
                {{ scope.row.type === 'class' ? scope.row.enterDate : scope.row.checkInDate }}
              </template>
            </el-table-column>
            <el-table-column label="退房日期" width="110" align="center">
              <template #default="scope">
                {{ scope.row.type === 'class' ? scope.row.leaveDate : scope.row.checkOutDate }}
              </template>
            </el-table-column>
            <el-table-column label="当日状态" width="90" align="center">
              <template #default="scope">
                <el-tag :type="getStatusType(scope.row.status)" size="small">
                  {{ getStatusTag(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-dialog>

      <!-- 房态图弹窗 -->
      <el-dialog v-model="roomPanelVisible" :title="'房态图 - ' + (selectedDay ? selectedDay.dateStr : '')" width="80%" top="5vh" class="calendar-dialog">
        <div v-if="selectedDay" style="margin-bottom:15px;">
          <el-descriptions :column="4" border size="small">
            <el-descriptions-item label="日期">{{ selectedDay.dateStr }} {{ selectedDay.weekDay }}</el-descriptions-item>
            <el-descriptions-item label="在住人数">{{ selectedDay.stayCount }}</el-descriptions-item>
            <el-descriptions-item label="空房">{{ selectedDay.emptyRooms }}/{{ selectedDay.totalRooms }}</el-descriptions-item>
            <el-descriptions-item label="入住率">{{ selectedDay.occupancyRate }}%</el-descriptions-item>
          </el-descriptions>
        </div>
        <div v-if="selectedDay" style="display:flex;gap:8px;margin-bottom:15px;flex-wrap:wrap;">
          <el-tag v-for="cls in (selectedDay.dayClasses || [])" :key="cls.classId"
            :color="cls.color" style="color:#fff;border:none;">
            {{ cls.className }}({{ cls.studentCount }}人)
          </el-tag>
          <el-tag v-for="b in (selectedDay.dayBookings || [])" :key="b.bookingId"
            :color="b.classColor" style="color:#fff;border:none;" effect="dark">
            {{ b.className }}(预订{{ b.bedsCount }}床)
          </el-tag>
          <el-tag style="background:#fff;color:#333;border:1px solid #ddd;">空房({{ selectedDay.emptyRooms }})</el-tag>
        </div>
        <!-- 班级分配情况列表 -->
        <div class="calendar-table-wrap" v-if="roomPanelDetail.length > 0">
          <el-table :data="roomPanelDetail" border max-height="400">
            <el-table-column label="房间号" prop="dormroomId" width="90" />
            <el-table-column label="状态" width="80" align="center">
              <template #default="scope">
                <el-tag :type="scope.row.statusType" size="small">{{ scope.row.statusText }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="分配班级" min-width="120">
              <template #default="scope">
                <el-tag v-if="scope.row.classColor" :color="scope.row.classColor" style="color:#fff;border:none;" size="small">
                  {{ scope.row.className }}
                </el-tag>
                <span v-else style="color:#999">-</span>
              </template>
            </el-table-column>
            <el-table-column label="入住/退房日期" width="160" align="center">
              <template #default="scope">
                {{ scope.row.enterDate || '-' }} ~ {{ scope.row.leaveDate || '-' }}
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div v-else style="text-align:center;padding:40px;color:#999;">该日无房态数据</div>
      </el-dialog>
    </el-card>
  </div>
</template>
<script src="@/assets/js/RoomCalendar.js"></script>
<style scoped>
@import '../assets/css/RoomCalendar.css';
.is-today {
  font-weight: bold;
  color: #409EFF;
}

/* 手机版日历视图 */
@media screen and (max-width: 768px) {
  .calendar-card {
    margin: 8px !important;
  }

  .calendar-toolbar {
    flex-wrap: wrap;
    gap: 8px;
  }

  .export-btn {
    width: 100%;
  }

  .stat-row .el-col {
    margin-bottom: 8px;
  }

  .stat-mini-card .el-card__body {
    padding: 10px !important;
  }

  .stat-mini-card div:first-child {
    font-size: 12px !important;
  }

  .stat-mini-card div:last-child {
    font-size: 22px !important;
  }

  .calendar-table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .calendar-dialog .el-dialog {
    width: 95% !important;
    margin-top: 3vh !important;
  }
}
</style>
